import React, { useEffect, useState, useCallback } from "react";
import { contactSupportApi } from "../../services/api";
import { PageHeader, Input, Button, Toggle } from "../../components/ui";
import toast from "react-hot-toast";
import {
  CheckCircle,
  XCircle,
  Phone,
  MessageCircle,
  Mail,
  Info,
  RotateCcw,
  Save,
} from "lucide-react";

export default function ContactSupportInfo() {
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [toggling, setToggling] = useState(false);
  const [form,     setForm]     = useState({
    title: "", description: "", email: "", phone: "", whatsapp: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await contactSupportApi.get();
      const d = r.data.data;
      setData(d);
      setForm({
        title:       d.title       || "",
        description: d.description || "",
        email:       d.email       || "",
        phone:       d.phone       || "",
        whatsapp:    d.whatsapp    || "",
      });
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load contact info");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const validate = () => {
    if (!form.title.trim())                               { toast.error("Title is required");           return false; }
    if (!form.description.trim())                         { toast.error("Description is required");      return false; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))  { toast.error("Enter a valid email");          return false; }
    if (!form.phone.trim())                               { toast.error("Phone is required");            return false; }
    if (!form.whatsapp.trim())                            { toast.error("WhatsApp number is required"); return false; }
    return true;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      await contactSupportApi.update(form);
      toast.success("Contact info updated");
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save");
    }
    setSaving(false);
  };

  const handleToggle = async (val) => {
    setToggling(true);
    try {
      await contactSupportApi.toggle(val);
      setData((prev) => ({ ...prev, active: val }));
      toast.success(val ? "Contact info activated" : "Contact info deactivated");
    } catch {
      toast.error("Failed to toggle status");
    }
    setToggling(false);
  };

  const field = (key) => ({
    value:    form[key],
    onChange: (e) => setForm((f) => ({ ...f, [key]: e.target.value })),
  });

  // ── Skeleton ──────────────────────────────────────────────────────────────
  if (loading) return (
    <div>
      <style>{`@keyframes shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}`}</style>
      <PageHeader title="Contact Us" subtitle="Manage the in-app contact support information" />
      <div style={{ maxWidth: 640, display: "flex", flexDirection: "column", gap: 14 }}>
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} style={{ height: 52, borderRadius: 8, background: "linear-gradient(90deg,var(--border) 25%,rgba(15,61,62,0.05) 50%,var(--border) 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.5s ease-in-out infinite" }} />
        ))}
      </div>
    </div>
  );

  return (
    <div>
      <PageHeader title="Contact Us" subtitle="Manage the in-app contact support information shown to users" />

      <div style={{ maxWidth: 640 }}>
        {/* Status Banner */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderRadius: 10, marginBottom: 20, background: data?.active ? "rgba(34,197,94,0.08)" : "rgba(239,68,68,0.06)", border: `1px solid ${data?.active ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.2)"}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {data?.active
              ? <CheckCircle size={20} strokeWidth={1.5} color="#22c55e" />
              : <XCircle    size={20} strokeWidth={1.5} color="#ef4444" />
            }
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }}>
                {data?.active ? "Contact info is Active" : "Contact info is Inactive"}
              </div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 1 }}>
                {data?.active ? "Visible to users in the app" : "Hidden from users in the app"}
              </div>
            </div>
          </div>
          <Toggle value={!!data?.active} onChange={handleToggle} disabled={toggling} />
        </div>

        {/* Form Card */}
        <div style={{ background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden" }}>
          {/* Card Header */}
          <div style={{ padding: "12px 18px", borderBottom: "1px solid var(--border)", background: "rgba(15,61,62,0.05)", display: "flex", alignItems: "center", gap: 8 }}>
            <Phone size={16} strokeWidth={2} color="var(--text-primary)" />
            <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }}>Support Contact Details</span>
            <span style={{ marginLeft: "auto", fontSize: 10, fontWeight: 600, letterSpacing: "0.5px", textTransform: "uppercase", color: "var(--text-muted)" }}>
              Last updated: {data?.updatedAt ? new Date(data.updatedAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
            </span>
          </div>

          <form onSubmit={handleSave} style={{ padding: 18, display: "flex", flexDirection: "column", gap: 14 }}>

            <Input label="Title" placeholder="e.g. Still have questions?" {...field("title")} required />

            {/* Description */}
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.6px", display: "block", marginBottom: 6 }}>
                Description
              </label>
              <textarea
                rows={3}
                placeholder="e.g. Our support team is available 24/7 to help you"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                required
                style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--bg-input)", color: "var(--text-primary)", fontSize: 13, fontFamily: "var(--font-body)", resize: "vertical", outline: "none", lineHeight: 1.6 }}
              />
            </div>

            <Input label="Email" type="email" placeholder="support@example.com" {...field("email")} required />

            {/* Phone + WhatsApp side by side */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.6px", display: "block", marginBottom: 6 }}>
                  Phone
                </label>
                <div style={{ position: "relative" }}>
                  <Phone size={14} strokeWidth={2} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: "var(--text-muted)" }} />
                  <input
                    type="tel" placeholder="+91XXXXXXXXXX"
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                    required
                    style={{ width: "100%", paddingLeft: 32, paddingRight: 12, paddingTop: 9, paddingBottom: 9, borderRadius: 8, border: "1px solid var(--border)", background: "var(--bg-input)", color: "var(--text-primary)", fontSize: 13, fontFamily: "var(--font-body)", outline: "none" }}
                  />
                </div>
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.6px", display: "block", marginBottom: 6 }}>
                  WhatsApp
                </label>
                <div style={{ position: "relative" }}>
                  <MessageCircle size={14} strokeWidth={2} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: "var(--text-muted)" }} />
                  <input
                    type="tel" placeholder="+91XXXXXXXXXX"
                    value={form.whatsapp}
                    onChange={(e) => setForm((f) => ({ ...f, whatsapp: e.target.value }))}
                    required
                    style={{ width: "100%", paddingLeft: 32, paddingRight: 12, paddingTop: 9, paddingBottom: 9, borderRadius: 8, border: "1px solid var(--border)", background: "var(--bg-input)", color: "var(--text-primary)", fontSize: 13, fontFamily: "var(--font-body)", outline: "none" }}
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{ borderTop: "1px solid var(--border)", paddingTop: 14, display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <Button variant="ghost" type="button" onClick={load} disabled={saving}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                  <RotateCcw size={13} strokeWidth={2.5} /> Reset
                </span>
              </Button>
              <Button type="submit" disabled={saving}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                  <Save size={13} strokeWidth={2.5} /> {saving ? "Saving…" : "Save Changes"}
                </span>
              </Button>
            </div>
          </form>
        </div>

        {/* Info Note */}
        <div style={{ marginTop: 14, padding: "10px 14px", borderRadius: 8, background: "rgba(15,61,62,0.05)", border: "1px solid rgba(15,61,62,0.12)", fontSize: 12, color: "var(--text-muted)", display: "flex", gap: 8, alignItems: "flex-start" }}>
          <Info size={14} strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>This is a singleton record — there is always exactly one contact support document. Changes here reflect immediately in the app once active.</span>
        </div>
      </div>
    </div>
  );
}